import React, { useRef, useState, useEffect } from "react";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import { mdiEarth, mdiChevronDown } from "@mdi/js";
import useICSImport from "../../hooks/useICSImport";
import ConflictModal from "../shared/ConflictModal";
import ICSFileUpload from "../shared/ICSFileUpload";
import ImageUpload from "../shared/ImageUpload";
import LocationPickerModal from '../LocationPickerModal';
import DateInputWithPicker from "../DateInputWithPicker";
import TimeInputWithPicker from "../TimeInputWithPicker";
import TimezonePicker from "../TimezonePicker";
import { zoneOptionLabel } from "../../utils/timezoneLabel";
import { splitDateTimeLocal, joinDateTimeLocal, isoToLocalDateTimeInput } from "../../utils/datetimeLocal";
import './EventDetailsForm.css';

/**
 * EventDetailsForm - Step 1 of ticket creation wizard
 * Allows manual event entry, ICS file import with conflict resolution, and image upload
 */
const EventDetailsForm = ({ formData, updateFormSection, isLoading, setError, onOwnershipConflict, user, editMode = false, orgTimezone = null }) => {
    const fileInputRef = useRef(null);
    const [eventImage, setEventImage] = useState(null);
    const [showLocationPicker, setShowLocationPicker] = useState(false);

    // Event-timezone picker: open state + position lifted here (the picker portals to body,
    // positioned from the trigger's bounding rect — mirrors SingleMonthDatePicker usage).
    const tzTriggerRef = useRef(null);
    const [tzPickerOpen, setTzPickerOpen] = useState(false);
    const [tzPickerPosition, setTzPickerPosition] = useState({ top: 0, left: 0 });
    const openTzPicker = () => {
        if (tzTriggerRef.current) {
            const rect = tzTriggerRef.current.getBoundingClientRect();
            setTzPickerPosition({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
        }
        setTzPickerOpen(true);
    };

    // orgTimezone is fetched asynchronously by the parent and may still be null when the
    // ICS import callback (captured once by useICSImport) fires. Mirror it into a ref that
    // always holds the latest value, so the import converts ICS times using the real org
    // timezone instead of falling back to browser-local/UTC on a fast upload (the bug where
    // imported event times showed in UTC).
    const orgTimezoneRef = useRef(orgTimezone);
    useEffect(() => {
        orgTimezoneRef.current = orgTimezone;
    }, [orgTimezone]);

    // Initialize ICS import hook with single event mode for ticket wizard
    const {
        isUploading: isICSProcessing,
        uploadStatus: icsStatus,
        pendingConflicts,
        showConflictModal,
        processICalFile,
        handleConflictResolutions,
        clearEvents: clearICSState
    } = useICSImport({
        mode: "single",
        filterFacebookEvents: false, // Allow any calendar events, not just Facebook
        currentUserOrgId: user?.organisationId, // CRITICAL: Pass current user's org ID for ownership checking
        onEventsImported: (importedEvents) => {
            if (importedEvents.length > 0) {
                const event = importedEvents[0];
                populateFormFromEvent(event);
            }
        },
        onOwnershipConflict: onOwnershipConflict // NEW: Pass ownership conflict handler from TicketWizard
    });

    const eventDetails = formData.eventDetails || {};

    // Load existing event image using Base64 API (CORS-free solution)
    useEffect(() => {
        const loadEventImage = async () => {
            const imageKey = eventDetails.eventImageKey;

            if (!imageKey || eventImage) {
                return; // No image key or already loaded
            }

            try {
                const webApiClient = require('../../api/client').default;
                const response = await webApiClient.get(`/api/v2/event/image-base64?imageKey=${encodeURIComponent(imageKey)}`);

                if (response.data?.success && response.data?.imageBase64) {
                    setEventImage({
                        compressedBase64: response.data.imageBase64,
                        thumbnail: response.data.imageBase64
                    });
                } else {
                    console.warn("Base64 API response missing imageBase64 data");
                }
            } catch (error) {
                console.error("Failed to load event image:", error);
                setError?.("Failed to load event image");
            }
        };

        loadEventImage();
    }, [eventDetails.eventImageKey, eventImage]);

    // Helper function to populate form from imported event data
    const populateFormFromEvent = (event) => {
        // Backend returns UTC ISO strings (e.g., "2026-06-29T01:00:00.000Z").
        // Convert each UTC instant to the org's local wall-clock (falling back to the
        // browser's local zone when the org timezone hasn't resolved yet) so the
        // pre-filled time matches the org's location. A raw slice would keep the UTC
        // digits and show the wrong local time (e.g. 1:00 PM instead of 1:00 PM NZ
        // for a 01:00 UTC event).
        // Read the timezone from the ref (always current) — not the closure value, which
        // may be stale/null inside the once-captured ICS import callback.
        const tz = orgTimezoneRef.current;
        const eventData = {
            title: event.title,
            description: event.description || "",
            startDateTime: isoToLocalDateTimeInput(event.eventStartDateTime, tz),
            endDateTime: isoToLocalDateTimeInput(event.eventEndDateTime, tz),
            location: {
                description: event.eventLocation?.description || "",
                address: event.eventLocation?.address || "",
                latitude: event.eventLocation?.latitude || null,
                longitude: event.eventLocation?.longitude || null,
            },
            importSource: "ics",
            icsData: event.icsData || {
                originalUrl: event.facebookUrl,
                uid: event.icalUID,
                filename: "imported-event.ics",
            },
        };

        // Include image key if event has one (for ownership claim scenario)
        if (event.eventImageKey) {
            eventData.eventImageKey = event.eventImageKey;
        }

        updateFormSection("eventDetails", eventData);
        setError(""); // Clear any previous errors
    };

    const handleInputChange = (field, value) => {
        updateFormSection("eventDetails", {
            [field]: value,
        });
    };

    // Update one half (date or time) of a combined "YYYY-MM-DDTHH:MM" datetime
    // field, preserving the other half. The merged value is written back to the
    // same field so the submit/ICS round-trip is unchanged.
    const handleDateTimePartChange = (field, part, partValue) => {
        const current = splitDateTimeLocal(eventDetails[field]);
        const next = part === "date"
            ? joinDateTimeLocal(partValue, current.time)
            : joinDateTimeLocal(current.date, partValue);
        handleInputChange(field, next);
    };

    const handleLocationSave = (locationData) => {
        updateFormSection("eventDetails", {
            location: {
                description: locationData.description,
                address: locationData.address || locationData.description,
                latitude: locationData.latitude,
                longitude: locationData.longitude,
            },
        });
        setShowLocationPicker(false);
    };

    // Handle image upload
    const handleImageSelect = (imageData, error) => {
        if (error) {
            setError(`Image upload error: ${error.message || error}`);
            return;
        }
        
        if (imageData) {
            setEventImage(imageData); // Keep locally for UI preview only
            // Send only base64Image string to backend (not nested object with thumbnail)
            // Remove data:image prefix before sending to backend
            const base64String = imageData.compressedBase64.split(",")[1];
            updateFormSection("eventDetails", {
                base64Image: base64String,
            });
            setError(""); // Clear any previous errors
        }
    };

    // Enhanced ICS file processing with conflict resolution
    const handleAdvancedICSImport = async (content, filename) => {
        setError(""); // Clear previous errors
        
        try {
            const result = await processICalFile(content);
            
            if (!result) {
                // Error already set by processICalFile
                return;
            }

            if (result.conflicts && result.conflicts.length > 0) {
                // Conflicts will be handled by the ConflictModal
                return;
            }

            if (result.events && result.events.length > 0) {
                // Event already populated by onEventsImported callback
                setError(""); // Ensure no error state
            }
        } catch (error) {
            console.error("Error processing ICS file:", error);
            setError(`Failed to process ICS file: ${error.message}`);
        }
    };

    // Handle conflict resolution from ICS import
    const handleConflictResolution = (resolutions) => {
        const eventsToImport = handleConflictResolutions(resolutions);
        
        if (eventsToImport.length > 0) {
            // Take the first resolved event for single mode
            populateFormFromEvent(eventsToImport[0]);
        }
    };

    const clearImportedData = () => {
        updateFormSection("eventDetails", {
            title: "",
            description: "",
            startDateTime: "",
            endDateTime: "",
            location: { description: "", latitude: null, longitude: null },
            importSource: "manual",
            icsData: null,
        });
        clearICSState();
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="event-details-form">
            <h2>Event Details</h2>
            <p className="form-description">Create your event details manually or import from an ICS calendar file.</p>

            {/* Enhanced ICS Import Section with Conflict Resolution */}
            {/* Hide ICS import in edit mode - you're already editing an event */}
            {!editMode && (
                <div className="form-section">
                    <h3>Import from Calendar</h3>
                    <p className="form-help">Import event details from Google Calendar, Outlook, or any ICS file.</p>

                    <ICSFileUpload
                        onFileSelect={handleAdvancedICSImport}
                        isUploading={isICSProcessing}
                        uploadStatus={icsStatus}
                        buttonText={isICSProcessing ? "Processing Calendar..." : "Browse for Calendar File"}
                    />

                    {eventDetails.importSource === "ics" && eventDetails.icsData && (
                        <div className="import-info">
                            <h4>Imported from:</h4>
                            <p><strong>File:</strong> {eventDetails.icsData.filename}</p>
                            {eventDetails.icsData.originalUrl && (
                                <p>
                                    <strong>Original URL:</strong>{" "}
                                    <a href={eventDetails.icsData.originalUrl} target="_blank" rel="noopener noreferrer">
                                        {eventDetails.icsData.originalUrl}
                                    </a>
                                </p>
                            )}
                        </div>
                    )}

                    {eventDetails.importSource === "ics" && (
                        <button
                            type="button"
                            onClick={clearImportedData}
                            className="btn-turquoise btn-small"
                            disabled={isICSProcessing}>
                            Clear Import
                        </button>
                    )}
                </div>
            )}

            {/* Event Image Upload Section */}
            <div className="form-section" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <h3>Event Image</h3>
                <p className="form-help">Add an eye-catching image for your event (optional).</p>
                
                <ImageUpload 
                    onImageSelect={handleImageSelect}
                    currentImage={eventImage}
                    placeholder="Click to Upload Event Image"
                />
            </div>

            <div className="form-divider">
                <span>OR</span>
            </div>

            {/* Manual Event Entry */}
            <div className="form-section">
                <h3>Manual Entry</h3>

                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="event-title">Event Title *</label>
                        <input
                            id="event-title"
                            type="text"
                            value={eventDetails.title || ""}
                            onChange={e => handleInputChange("title", e.target.value)}
                            placeholder="Enter event title"
                            className="form-input"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="event-description">Description</label>
                        <textarea
                            id="event-description"
                            value={eventDetails.description || ""}
                            onChange={e => handleInputChange("description", e.target.value)}
                            placeholder="Event description..."
                            className="form-textarea"
                            rows={4}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group half-width">
                        <label>Event Start *</label>
                        <div className="datetime-picker-pair">
                            <DateInputWithPicker
                                id="start-date"
                                value={splitDateTimeLocal(eventDetails.startDateTime).date}
                                onChange={dateVal => handleDateTimePartChange("startDateTime", "date", dateVal)}
                                placeholder="Select date"
                            />
                            <TimeInputWithPicker
                                id="start-time"
                                value={splitDateTimeLocal(eventDetails.startDateTime).time}
                                onChange={timeVal => handleDateTimePartChange("startDateTime", "time", timeVal)}
                            />
                        </div>
                    </div>
                    <div className="form-group half-width">
                        <label>Event End *</label>
                        <div className="datetime-picker-pair">
                            <DateInputWithPicker
                                id="end-date"
                                value={splitDateTimeLocal(eventDetails.endDateTime).date}
                                onChange={dateVal => handleDateTimePartChange("endDateTime", "date", dateVal)}
                                placeholder="Select date"
                                fallbackMonth={splitDateTimeLocal(eventDetails.startDateTime).date
                                    ? DateTime.fromISO(splitDateTimeLocal(eventDetails.startDateTime).date)
                                    : null}
                            />
                            <TimeInputWithPicker
                                id="end-time"
                                value={splitDateTimeLocal(eventDetails.endDateTime).time}
                                onChange={timeVal => handleDateTimePartChange("endDateTime", "time", timeVal)}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group full-width">
                        <label>Event timezone</label>
                        <p className="form-hint">
                            The times above are shown and sold in this timezone, whichever timezone attendees are in.
                        </p>
                        <button
                            type="button"
                            ref={tzTriggerRef}
                            className="event-timezone-trigger"
                            onClick={openTzPicker}
                        >
                            <span className="event-timezone-trigger-label">
                                <Icon path={mdiEarth} size={0.8} />
                                {zoneOptionLabel(eventDetails.timezone || orgTimezone) || "Select timezone"}
                            </span>
                            <Icon path={mdiChevronDown} size={0.8} />
                        </button>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="event-location">Event Location *</label>

                        {eventDetails.location?.description ? (
                            // Display selected location with edit capability
                            <div className="selected-location-display">
                                <div className="location-display-content">
                                    <div className="location-display-description">
                                        {eventDetails.location.description}
                                    </div>
                                    {eventDetails.location.address && (
                                        <div className="location-display-address">
                                            {eventDetails.location.address}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowLocationPicker(true)}
                                    className="btn-change-location"
                                >
                                    Change
                                </button>
                            </div>
                        ) : (
                            // Show selection button when no location set
                            <button
                                type="button"
                                onClick={() => setShowLocationPicker(true)}
                                className="btn-select-location"
                            >
                                + Select Event Venue
                            </button>
                        )}

                        <small className="form-help">
                            Select venue location for accurate event search results
                        </small>
                    </div>
                </div>
            </div>

            {/* Form Validation Summary */}
            {/* <div className="form-validation">
                <h4>Required fields:</h4>
                <ul>
                    <li className={eventDetails.title ? "valid" : "invalid"}>
                        Event Title {eventDetails.title ? "✓" : ""}
                    </li>
                    <li className={eventDetails.startDateTime ? "valid" : "invalid"}>
                        Start Date & Time {eventDetails.startDateTime ? "✓" : ""}
                    </li>
                    <li className={eventDetails.endDateTime ? "valid" : "invalid"}>
                        End Date & Time {eventDetails.endDateTime ? "✓" : ""}
                    </li>
                    <li className={eventDetails.location?.description ? "valid" : "invalid"}>
                        Event Location {eventDetails.location?.description ? "✓" : ""}
                    </li>
                </ul>
            </div> */}
            
            {/* Conflict Resolution Modal */}
            {showConflictModal && pendingConflicts.length > 0 && (
                <ConflictModal
                    conflicts={pendingConflicts}
                    onResolve={handleConflictResolution}
                />
            )}

            {/* Location Picker Modal */}
            <LocationPickerModal
                isOpen={showLocationPicker}
                onClose={() => setShowLocationPicker(false)}
                onSave={handleLocationSave}
                currentLocation={eventDetails.location?.description ? {
                    description: eventDetails.location.description,
                    address: eventDetails.location.address || eventDetails.location.description,
                    latitude: eventDetails.location.latitude,
                    longitude: eventDetails.location.longitude
                } : null}
                mode="address"
                title="Select Event Venue"
            />

            <TimezonePicker
                isOpen={tzPickerOpen}
                position={tzPickerPosition}
                value={eventDetails.timezone || ""}
                defaultZone={orgTimezone}
                onSelect={(z) => handleInputChange("timezone", z)}
                onClose={() => setTzPickerOpen(false)}
            />
        </div>
    );
};

export default EventDetailsForm;
